import React, { useCallback, useState } from "react";
import "./BookingList.css";
import { FaTimes } from "react-icons/fa"; // Changed to FaTimes for cross icon
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  REFUND_WINDOW_HOURS,
  refundWindowMessage,
  hoursUntilBookingMYT,
} from "../constants";

// Notification Modal Component
const NotificationModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Notification</h2>
        <p>{message}</p>
        <button className="modal-close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} /> Got It
        </button>
      </div>
    </div>
  );
};

const BookingList = ({
  userBookings,
  userLessons,
  onAddToGoogleCalendar,
  onRefund,
  refundEligibility,
  // Shorter for coaches. This gate is ANDed with refundEligibility below, so it
  // has to know the caller's window too or a coach's button stays greyed out.
  cancelWindowHours = REFUND_WINDOW_HOURS,
}) => {
  const [notification, setNotification] = useState({ message: "", show: false });

  const parseDate = (dateStr) => {
    const [day, month, year] = dateStr.split("/").map(Number);
    const date = new Date(year, month - 1, day);
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return {
      dayOfWeek: days[date.getDay()],
      month: months[date.getMonth()],
      date: date.getDate(),
    };
  };

  const formatTimeRange = (time) => {
    const [startTime] = time.split(" - "); // Extract start time (e.g., "12:00 AM" from "12:00 AM - 1:00 AM")
    const [hour, minute, period] = startTime.split(/[: ]/);
    let startHour = parseInt(hour);
    if (period === "PM" && startHour !== 12) startHour += 12;
    if (period === "AM" && startHour === 12) startHour = 0;
    const startDate = new Date();
    startDate.setHours(startHour, parseInt(minute));
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
    const formatTime = (date) => {
      let hour = date.getHours();
      const minute = date.getMinutes().toString().padStart(2, "0");
      const period = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;
      return `${hour}:${minute} ${period}`;
    };
    let timeRange = `${formatTime(startDate)} - ${formatTime(endDate)}`;
    
    // Append "(Next Day)" for 12:00 AM and 6:00 AM start slots (UI only)
    if (startTime === "12:00 AM" || startTime === "01:00 AM" || startTime === "02:00 AM" || startTime === "03:00 AM" || startTime === "04:00 AM" || startTime === "05:00 AM" || startTime === "06:00 AM") {
      timeRange += " (Next Day)";
    }
    
    return timeRange;
  };

  const parseBookingDateTime = (date, time, adjustNextDay = false) => {
    const [day, month, year] = date.split("/").map(Number);
    const [startTime] = time.split(" - "); // Extract start time (e.g., "12:00 AM" from "12:00 AM - 1:00 AM")
    const [hour, minute, period] = startTime.split(/[: ]/);
    let h = parseInt(hour);
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;

    // Adjust date for 12:00 AM and 1:00 AM if needed (e.g., for calendar events)
    let adjustedDay = day;
    if (adjustNextDay && (startTime === "12:00 AM" || startTime === "01:00 AM" || startTime === "02:00 AM" || startTime === "03:00 AM" || startTime === "04:00 AM" || startTime === "05:00 AM" || startTime === "06:00 AM")) {
      adjustedDay += 1;
      // Handle month/year rollover
      const tempDate = new Date(year, month - 1, adjustedDay);
      adjustedDay = tempDate.getDate();
      if (tempDate.getMonth() + 1 !== month) {
        return new Date(year, month, 1, h, parseInt(minute));
      }
    }

    // Create Date object in local time (Malaysia, UTC+8 implicitly handled by browser)
    const bookingDate = new Date(year, month - 1, adjustedDay, h, parseInt(minute));
    return bookingDate;
  };

  const isRefundEligibleByTime = (createdAt, date, time) => {
    const hoursUntilBooking = hoursUntilBookingMYT(date, time);
    return {
      eligible: hoursUntilBooking > cancelWindowHours,
      message:
        hoursUntilBooking <= cancelWindowHours
          ? refundWindowMessage(cancelWindowHours)
          : "",
    };
  };

  const allBookings = [...userBookings, ...userLessons].sort((a, b) => {
    const dateA = parseBookingDateTime(a.date, a.time, false);
    const dateB = parseBookingDateTime(b.date, b.time, false);
    return dateA - dateB;
  });

  const handleAddToGoogleCalendar = (item) => {
    // Adjust date for 12:00 AM and 1:00 AM slots when adding to calendar
    const adjustedStartTime = parseBookingDateTime(item.date, item.time, true);
    const adjustedEndTime = new Date(adjustedStartTime.getTime() + 60 * 60 * 1000); // 1 hour duration

    // Format times for Google Calendar URL (local time, no UTC offset)
    const formatForGoogleCalendar = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-based
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };

    const start = formatForGoogleCalendar(adjustedStartTime);
    const end = formatForGoogleCalendar(adjustedEndTime);

    const eventTitle = item.type === "Bookings" ? `Court Booking - ${item.name}` : `Lesson - ${item.name}`;
    const calendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${start}/${end}&ctz=Asia/Kuala_Lumpur`;

    window.open(calendarUrl, "_blank");
  };

  const handleCloseNotification = () => {
    setNotification({ message: "", show: false });
  };

  const handleDeleteBooking = useCallback(async (bookingId) => {
    const booking = allBookings.find(b => b.id === bookingId);
    if (!booking) {
      setNotification({
        message: "Booking not found. Please refresh and try again.",
        show: true,
      });
      return;
    }

    const eligibility = refundEligibility[bookingId];
    const confirmMessage = booking.order_id
      ? `Are you sure you want to refund this booking? ${eligibility?.estimatedRefund ? `Approximately RM ${eligibility.estimatedRefund.toFixed(2)} will be credited to your account.` : 'A proportional amount will be credited to your account.'}`
      : "Are you sure you want to cancel this free booking?";

    const confirmDelete = window.confirm(confirmMessage);
    if (!confirmDelete) return;

    try {
      const refundResponse = await onRefund(bookingId);
      if (refundResponse && !refundResponse.error) {
        // No need for window.location.reload() since ProfilePage.js handles the refresh
        setNotification({
          message: refundResponse.message || "Booking canceled successfully.",
          show: true,
        });
      } else {
        setNotification({
          message: "Cancellation failed: " + (refundResponse?.message || "Unknown error"),
          show: true,
        });
      }
    } catch (error) {
      console.error("Unexpected error during cancellation:", error.message, error.stack);
      setNotification({
        message: "An unexpected error occurred: " + error.message,
        show: true,
      });
    }
  }, [onRefund, allBookings, refundEligibility]);

  const renderBookingItem = (item) => {
    const eligibility = refundEligibility[item.id] || { eligible: false, message: "Unable to check refund status." };
    const timeEligibility = isRefundEligibleByTime(item.created_at, item.date, item.time);
    const isRefundDisabled = !eligibility.eligible || !timeEligibility.eligible;
    const ineligibilityMessage = !eligibility.eligible ? eligibility.message : timeEligibility.message;
    const { dayOfWeek, month, date } = parseDate(item.date);
    const timeRange = formatTimeRange(item.time);

    let title;
    if (item.type === "Bookings") {
      title = `Court Booking - ${item.name}`;
    } else if (item.type === "Lessons") {
      if (item.lessonType === "Group") {
        const sportPrefix = item.sport && item.sport !== "Unknown" ? `${item.sport} ` : "";
        title = `Group Lesson - ${sportPrefix}${item.name} (${item.level || "Unknown"})`;
      } else if (item.lessonType === "Private") {
        const sportPrefix = item.sport && item.sport !== "Unknown" ? `${item.sport} ` : "";
        title = `Private Lesson - ${sportPrefix}with ${item.coach}`;
      }
    }

    return (
      <div key={item.id} className="booking-card">
        <div className="booking-content">
          <div className="booking-title">{title}</div>
          <div className="booking-details">
            {dayOfWeek}, {month} {date}, {timeRange}
          </div>
          {item.type === "Lessons" && item.lessonType === "Group" && item.coach && <div className="booking-details">{item.coach}</div>}
        </div>
        <div className="booking-actions">
          <button
            className="action-button calendar-button"
            onClick={() => handleAddToGoogleCalendar(item)}
            title="Add to Google Calendar"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <path d="M16 2v4" />
              <path d="M8 2v4" />
              <path d="M3 10h18" />
            </svg>
          </button>
          <button
            className={`action-button refund-button ${isRefundDisabled ? "ineligible" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isRefundDisabled) {
                setNotification({
                  message: ineligibilityMessage,
                  show: true,
                });
                return;
              }
              handleDeleteBooking(item.id);
            }}
            title={isRefundDisabled ? ineligibilityMessage : item.order_id ? `Request Refund for This Booking (${eligibility.estimatedRefund ? `~RM ${eligibility.estimatedRefund.toFixed(2)}` : 'Proportional Amount'})` : "Cancel Free Booking"}
            aria-label={item.order_id ? "Request Refund for This Booking" : "Cancel Free Booking"}
          >
            <FaTimes />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="booking-list">
      {allBookings.length === 0 ? (
        <div className="no-items">No bookings found.</div>
      ) : (
        allBookings.map((booking) => renderBookingItem(booking))
      )}
      <NotificationModal
        isOpen={notification.show}
        onClose={handleCloseNotification}
        message={notification.message}
      />
    </div>
  );
};

export default BookingList;